(() => {
  /**
   * Custom element principal que gerencia as telas do jogo
   */
  class WordGameSection extends HTMLElement {
    constructor() {
      super();
      /** @type {number} */
      this.currentScreen = 1;
      /** @type {string | null} */
      this.gameResult = null; // 'win' or 'lose'
      /** @type {WordleGameComponent | null} */
      this.wordleGame = null;
      /** @type {boolean} */
      this.cameFromGame = false; // Indica se veio da tela 3 (jogo)
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Usa event delegation para capturar cliques em todos os botões
      this.addEventListener('click', (e) => {
        // Encontra o botão mais próximo (pode ser o próprio elemento ou um ancestral)
        const button = e.target.closest && e.target.closest('.word-game-btn, .word-game-help-btn');
        
        if (button) {
          const action = button.getAttribute('data-action');
          if (action) {
            e.preventDefault();
            e.stopPropagation();
            this.handleAction(action);
            return false;
          }
        }
      });

      // Inicializa o componente do jogo quando a tela 3 for ativada
      this.observeScreenChanges();
    }

    observeScreenChanges() {
      // Verifica imediatamente se a tela 3 já está ativa
      const screen3 = this.querySelector('.screen-3');
      if (screen3 && screen3.classList.contains('active')) {
        this._initGame();
      }

      const observer = new MutationObserver(() => {
        const screen3 = this.querySelector('.screen-3');
        if (screen3 && screen3.classList.contains('active') && !this.wordleGame) {
          this._initGame();
        }
      });

      observer.observe(this, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
    }

    _initGame() {
      const gameElement = this.querySelector('wordle-game-component');
      if (gameElement && !gameElement.initialized) {
        this.wordleGame = new WordleGameComponent(gameElement, this);
        gameElement.initialized = true;
      }
    }

    /**
     * Centraliza a seção do jogo no centro da viewport
     */
    centerSectionInViewport() {
      const section = this.closest('.word-game-section') || this;
      
      if (section && typeof section.scrollIntoView === 'function') {
        // Usa scrollIntoView com comportamento suave e centralizado
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      } else {
        // Fallback: calcula manualmente a posição para centralizar
        const rect = section.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const sectionHeight = rect.height;
        const targetScroll = scrollTop + rect.top - (windowHeight / 2) + (sectionHeight / 2);
        
        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }

    handleAction(action) {
      if (!action) return;
      
      switch(action) {
        case 'go-to-instructions':
          this.cameFromGame = false;
          this.centerSectionInViewport();
          this.showScreen(2);
          break;
        case 'go-to-instructions-from-game':
          this.cameFromGame = true;
          this.showScreen(2);
          break;
        case 'go-to-game':
          this.cameFromGame = false;
          this.showScreen(3);
          break;
        case 'back-to-game':
          this.showScreen(3);
          break;
        case 'win':
          this.gameResult = 'win';
          this.showScreen(4);
          break;
        case 'lose':
          this.gameResult = 'lose';
          this.showScreen(4);
          break;
        case 'restart':
          this.gameResult = null;
          this.cameFromGame = false;
          if (this.wordleGame) {
            this.wordleGame.restart();
          }
          this.showScreen(1);
          break;
      }
    }

    showScreen(screenNumber) {
      // Esconde todas as telas
      const allScreens = this.querySelectorAll('.word-game-screen');
      allScreens.forEach(screen => {
        screen.classList.remove('active');
      });

      // Mostra a tela desejada
      const targetScreen = this.querySelector(`.screen-${screenNumber}`);
      if (targetScreen) {
        targetScreen.classList.add('active');
        this.currentScreen = screenNumber;

        // Se for a tela 3, inicializa o jogo
        if (screenNumber === 3) {
          this._initGame();
        }

        // Se for a tela 2, atualiza o botão baseado na origem
        if (screenNumber === 2) {
          this.updateInstructionsButton();
        }

        // Se for a tela 4, atualiza o texto baseado no resultado
        if (screenNumber === 4) {
          this.updateResultScreen();
        }
      }
    }

    updateInstructionsButton() {
      const btn = this.querySelector('.btn-start-game');
      const btnTextStart = this.querySelector('.btn-text-start');
      const btnTextBack = this.querySelector('.btn-text-back');
      
      if (!btn) return;

      if (this.cameFromGame) {
        // Veio do jogo, mostra "VOLTAR AO JOGO"
        if (btnTextStart) btnTextStart.style.display = 'none';
        if (btnTextBack) btnTextBack.style.display = 'inline';
        btn.setAttribute('data-action', 'back-to-game');
      } else {
        // Veio da tela inicial, mostra "COMEÇAR"
        if (btnTextStart) btnTextStart.style.display = 'inline';
        if (btnTextBack) btnTextBack.style.display = 'none';
        btn.setAttribute('data-action', 'go-to-game');
      }
    }

    updateResultScreen() {
      // Usa seletores de classe para sempre encontrar os elementos
      const winTitle = this.querySelector('.word-game-result-title-win');
      const loseTitle = this.querySelector('.word-game-result-title-lose');

      if (this.gameResult === 'win') {
        if (winTitle) {
          winTitle.setAttribute('data-result-title', 'win');
          winTitle.style.display = 'flex';
        }
        if (loseTitle) {
          loseTitle.removeAttribute('data-result-title');
          loseTitle.style.display = 'none';
        }
      } else if (this.gameResult === 'lose') {
        if (winTitle) {
          winTitle.removeAttribute('data-result-title');
          winTitle.style.display = 'none';
        }
        if (loseTitle) {
          loseTitle.setAttribute('data-result-title', 'lose');
          loseTitle.style.display = 'flex';
        }
      }
    }

    onGameEnd(won) {
      this.gameResult = won ? 'win' : 'lose';
      this.showScreen(4);
    }
  }

  /**
   * Custom element que implementa a lógica do jogo Wordle
   */
  class WordleGameComponent {
    constructor(element, parentSection) {
      this.element = element;
      this.parentSection = parentSection;
      
      /** @type {string} Palavra-alvo da rodada atual */
      this.WORD = '';
      
      /** @type {string[]} Lista de palavras válidas */
      this.wordList = [];
      
      /** @type {number} Número máximo de tentativas */
      this.MAX_ATTEMPTS = 6;
      
      /** @type {number} Comprimento fixo de cada palavra */
      this.WORD_LENGTH = 5;
      
      /** @type {number} Intervalo em ms entre o flip de cada tile */
      this.FLIP_STEP = 350;
      
      /** @type {number} Duração em ms até o ponto médio do flip */
      this.FLIP_HALF = 175;
      
      /** @type {number} Índice da linha atual */
      this.currentRow = 0;
      
      /** @type {number} Índice da coluna atual */
      this.currentCol = 0;
      
      /** @type {boolean} Indica se o jogo terminou */
      this.gameOver = false;
      
      /** @type {Object.<string, 'correct'|'present'|'absent'>} Estado das letras no teclado */
      this.letterStates = {};

      this._loadWords();
      this.WORD = this._pickWord();
      this._bindKeyboard();
    }

    _loadWords() {
      const scriptEl = this.element.querySelector('.word-game-words-data');
      if (!scriptEl) return;

      const raw = (scriptEl.textContent || '').trim();

      try {
        const text = JSON.parse(raw || '""');
        const lines = typeof text === 'string' ? text.split(/\r?\n/) : [];

        this.wordList = lines
          .map(w => this._normalizeWord(w))
          .filter(w => w.length === this.WORD_LENGTH);
      } catch (_e) {
        // wordList permanece vazio
      }
    }

    _normalizeWord(word) {
      return String(word)
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
    }

    _pickWord() {
      if (this.wordList.length === 0) return '';
      if (this.wordList.length === 1) return this.wordList[0] ?? '';

      let candidate = this.WORD;
      let tries = 0;
      while (candidate === this.WORD && tries < 20) {
        candidate = this.wordList[Math.floor(Math.random() * this.wordList.length)] ?? '';
        tries++;
      }
      return candidate;
    }

    _bindKeyboard() {
      const keyboard = this.element.querySelector('#word-game-keyboard');
      if (keyboard) {
        keyboard.addEventListener('click', e => {
          if (!e.target) return;
          const btn = e.target.closest('[data-key]');
          const key = btn && btn.dataset.key;
          if (key) this._handleKey(key);
        });
      }
    }

    _handleKey(key) {
      if (this.gameOver) return;
      if (key === '⌫') {
        this._deleteLetter();
      } else if (key === 'ENTER') {
        this._submitGuess();
      } else {
        this._addLetter(key);
      }
    }

    _addLetter(letter) {
      if (this.currentCol >= this.WORD_LENGTH) return;
      const tile = this._tile(this.currentRow, this.currentCol);
      tile.textContent = letter;
      tile.classList.add('word-game-tile--filled');
      tile.classList.remove('word-game-pop');
      void tile.offsetWidth;
      tile.classList.add('word-game-pop');
      this.currentCol++;
    }

    _deleteLetter() {
      if (this.currentCol <= 0) return;
      this.currentCol--;
      const tile = this._tile(this.currentRow, this.currentCol);
      tile.textContent = '';
      tile.classList.remove('word-game-tile--filled', 'word-game-pop');
    }

    _submitGuess() {
      if (this.currentCol < this.WORD_LENGTH) {
        this._shakeRow(this.currentRow);
        this._showMessage('Palavra incompleta', true);
        return;
      }

      const letters = Array.from({ length: this.WORD_LENGTH }, (_, c) =>
        this._tile(this.currentRow, c).textContent
      );
      const guess = letters.join('');
      const evaluation = this._evaluate(guess);
      const rowSnap = this.currentRow;

      this._revealRow(rowSnap, evaluation, letters);

      const doneDelay = (this.WORD_LENGTH - 1) * this.FLIP_STEP + this.FLIP_HALF + 200;

      setTimeout(() => {
        if (guess === this.WORD) {
          this.gameOver = true;
          if (this.parentSection) {
            this.parentSection.onGameEnd(true);
          }
        } else if (rowSnap >= this.MAX_ATTEMPTS - 1) {
          this.gameOver = true;
          if (this.parentSection) {
            this.parentSection.onGameEnd(false);
          }
        }
      }, doneDelay);

      this.currentRow++;
      this.currentCol = 0;
    }

    _evaluate(guess) {
      const result = Array(this.WORD_LENGTH).fill('absent');
      const wordArr = this.WORD.split('');
      const guessArr = guess.split('');
      const wordUsed = Array(this.WORD_LENGTH).fill(false);
      const guessMatched = Array(this.WORD_LENGTH).fill(false);

      // 1ª passagem — posição exata
      for (let i = 0; i < this.WORD_LENGTH; i++) {
        if (guessArr[i] === wordArr[i]) {
          result[i] = 'correct';
          wordUsed[i] = true;
          guessMatched[i] = true;
        }
      }

      // 2ª passagem — letra presente em outra posição
      for (let i = 0; i < this.WORD_LENGTH; i++) {
        if (guessMatched[i]) continue;
        for (let j = 0; j < this.WORD_LENGTH; j++) {
          if (wordUsed[j]) continue;
          if (guessArr[i] === wordArr[j]) {
            result[i] = 'present';
            wordUsed[j] = true;
            break;
          }
        }
      }

      return result;
    }

    _revealRow(rowIndex, result, letters) {
      for (let c = 0; c < this.WORD_LENGTH; c++) {
        const tile = this._tile(rowIndex, c);
        const state = result[c] ?? 'absent';
        const letter = letters[c] ?? '';
        const delay = c * this.FLIP_STEP;

        setTimeout(() => {
          tile.classList.add('word-game-tile--flipping');
        }, delay);

        setTimeout(() => {
          tile.classList.add(`word-game-tile--${state}`);
          this._updateKeyState(letter, state);
        }, delay + this.FLIP_HALF);
      }
    }

    _updateKeyState(letter, newState) {
      const priority = { correct: 3, present: 2, absent: 1 };
      const current = this.letterStates[letter];

      if (current && priority[current] >= priority[newState]) return;

      this.letterStates[letter] = newState;

      const key = this.element.querySelector(`[data-key="${letter}"]`);
      if (!key) return;
      key.classList.remove('word-game-key--correct', 'word-game-key--present', 'word-game-key--absent');
      key.classList.add(`word-game-key--${newState}`);
    }

    _tile(row, col) {
      return this.element.querySelector(`#word-game-tile-${row}-${col}`);
    }

    _shakeRow(rowIndex) {
      const row = this.element.querySelector(`#word-game-row-${rowIndex}`);
      if (!row) return;
      row.classList.remove('word-game-shake');
      void row.offsetWidth;
      row.classList.add('word-game-shake');
    }

    _showMessage(text, autoHide) {
      const el = this.element.querySelector('#word-game-message');
      if (!el) return;
      el.textContent = text;
      el.classList.add('word-game-message--visible');
      if (autoHide) {
        setTimeout(() => el.classList.remove('word-game-message--visible'), 1500);
      }
    }

    restart() {
      this.WORD = this._pickWord();
      this.currentRow = 0;
      this.currentCol = 0;
      this.gameOver = false;
      this.letterStates = {};

      for (let r = 0; r < this.MAX_ATTEMPTS; r++) {
        for (let c = 0; c < this.WORD_LENGTH; c++) {
          const tile = this._tile(r, c);
          tile.textContent = '';
          tile.className = 'word-game-tile';
        }
      }

      this.element.querySelectorAll('.word-game-key').forEach(key => {
        key.classList.remove('word-game-key--correct', 'word-game-key--present', 'word-game-key--absent');
      });

      const msg = this.element.querySelector('#word-game-message');
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('word-game-message--visible');
      }
    }
  }

  customElements.define('word-game-section', WordGameSection);
})();
